import type { PayoutDetails, PayoutMethod } from "@artha/types";
import { PAYOUT_METHOD } from "@artha/types";
import { maskEmail } from "./email";

type PayoutDetailFields = Partial<PayoutDetails["details"]>;

const BANK_MASK = "XXXXXXXX";

const resolvePayoutInput = (
  payoutOrMethod?: PayoutMethod | Pick<PayoutDetails, "method" | "details"> | null,
  details?: PayoutDetailFields | null,
): { method?: PayoutMethod; details?: PayoutDetailFields | null } => {
  if (
    payoutOrMethod &&
    typeof payoutOrMethod === "object" &&
    "method" in payoutOrMethod
  ) {
    return {
      method: payoutOrMethod.method,
      details: payoutOrMethod.details,
    };
  }

  return {
    method: payoutOrMethod ?? undefined,
    details,
  };
};

// Used by revenue-hub UIs to show a masked payout method string.
export function maskBankAccount(account: string): string {
  const trimmedAccount = account.trim();
  const last4 =
    trimmedAccount.length > 4 ? trimmedAccount.slice(-4) : trimmedAccount;

  return `${BANK_MASK}${last4}`;
}

// Serialize a response copy so database documents and import rows stay unchanged.
export function maskPayoutBankDetails(response: unknown): unknown {
  if (response == null) return response;
  return JSON.parse(JSON.stringify(response, (key, value) =>
    (key === "bank_account" || key === "ifsc") &&
    (typeof value === "string" || typeof value === "number")
      ? maskBankAccount(String(value))
      : value,
  ));
}

// Used by frontend payout views to format either PayPal or bank details.
export function formatPayoutMethod(
  payoutOrMethod?: PayoutMethod | Pick<PayoutDetails, "method" | "details"> | null,
  details?: PayoutDetailFields | null,
): string {
  const resolved = resolvePayoutInput(payoutOrMethod, details);

  if (
    resolved.method === PAYOUT_METHOD.PAYPAL &&
    resolved.details?.paypal_email
  ) {
    return maskEmail(resolved.details.paypal_email);
  }

  if (resolved.details?.bank_account) {
    return maskBankAccount(resolved.details.bank_account);
  }

  return "Add Method";
}

// Used by backend receipt generation to display the beneficiary label safely.
export function formatReceiptBeneficiaryName(
  payoutDetails?: PayoutDetails | null,
): string {
  if (payoutDetails?.method === PAYOUT_METHOD.PAYPAL) {
    const email = payoutDetails.details?.paypal_email;

    return `PayPal: ${email ? maskEmail(email) : "N/A"}`;
  }

  return payoutDetails?.details?.beneficiary_name || "N/A";
}
